import {
  createTemplateTheme,
  type CodeComponentTemplateBranding,
} from './branding';

export function createPricingCalculatorSource(
  branding: CodeComponentTemplateBranding = {}
): string {
  const {themeSource, primaryColor} = createTemplateTheme(branding, '#3b82f6');

  return `import { declareComponent } from "@webflow/react";
import { props } from "@webflow/data-types";
import { useState } from "react";

${themeSource}

/* ── traffic tiers ── */
const TRAFFIC_STEPS = [
  { label: "10K", visitors: 10_000 },
  { label: "25K", visitors: 25_000 },
  { label: "50K", visitors: 50_000 },
  { label: "100K", visitors: 100_000 },
  { label: "250K", visitors: 250_000 },
  { label: "500K", visitors: 500_000 },
  { label: "1M", visitors: 1_000_000 },
];

function getPrice(plan, isYearly) {
  return isYearly ? plan.yearly : plan.monthly;
}

function formatUSD(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}


/* ── check icon ── */
function Check() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ── plan data ── */
const PLANS = [
  {
    key: "basic",
    name: "Basic",
    monthly: 25,
    yearly: 20,
    description: "For personal projects and small sites getting started.",
    features: [
      "Custom domain",
      "SSL certificate",
      "Basic analytics",
      "Email support",
      "5 team members",
    ],
    cta: "Start free trial",
    highlighted: false,
  },
  {
    key: "premium",
    name: "Premium",
    monthly: 50,
    yearly: 25,
    description: "For growing businesses that need more power and flexibility.",
    features: [
      "Everything in Basic",
      "Advanced analytics",
      "A/B testing",
      "Priority support",
      "25 team members",
      "Custom integrations",
    ],
    cta: "Start free trial",
    highlighted: true,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    monthly: 200,
    yearly: 150,
    description: "For large-scale operations with dedicated infrastructure.",
    features: [
      "Everything in Premium",
      "Dedicated account manager",
      "99.99% SLA uptime",
      "SSO & SAML",
      "Unlimited team members",
      "Custom contracts",
      "On-premise option",
    ],
    cta: "Contact sales",
    highlighted: false,
  },
];


/* ── main component ── */
export function PricingSection({
  heading = "Plans that scale with you",
  subheading = "Transparent pricing that grows alongside your traffic. No surprises, cancel anytime.",
  billingNote = "All plans billed monthly. Annual billing saves 20%.",
  primaryColor = ${primaryColor},
}) {
  const [stepIndex, setStepIndex] = useState(2);
  const [isYearly, setIsYearly] = useState(false);
  const percentage = (stepIndex / (TRAFFIC_STEPS.length - 1)) * 100;

  const handleSliderClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(ratio * (TRAFFIC_STEPS.length - 1));
    setStepIndex(Math.max(0, Math.min(TRAFFIC_STEPS.length - 1, idx)));
  };

  // Shadow DOM ignores Tailwind's @property defaults: set border-style
  // explicitly and avoid partial translate or shadow utilities.
  return (
    <div className="bg-[var(--template-surface)] px-6 py-20" style={themeStyle(primaryColor)}>
      {/* HEADING - Size: 3xl, Weight: 600 */}
      <div className="mx-auto mb-12 max-w-3xl text-center">
        <h2 className={"mb-3 " + TYPE.h1}>
          {heading}
        </h2>
        {/* BODY - Size: base, Weight: 400 */}
        <p className="mb-8 text-base text-[color:var(--template-muted)]">{subheading}</p>

        {/* 🎨 BRAND COLOR: Toggle active state */}
        <div className="relative inline-grid grid-cols-2 rounded-[var(--template-button-radius,0.5rem)] border border-solid border-[color:var(--template-border)] bg-[var(--template-subtle)] p-1">
          <div
            className="absolute bottom-1 top-1 rounded-[var(--template-button-radius,0.375rem)] transition-transform duration-300 ease-in-out"
            style={{
              width: "calc(50% - 4px)",
              left: 4,
              transform: isYearly ? "translateX(calc(100% + 4px))" : "translateX(0)",
              backgroundColor: primaryColor,
            }}
          />
          <button
            onClick={() => setIsYearly(false)}
            aria-pressed={!isYearly}
            className={"relative z-10 rounded-[var(--template-button-radius,0.375rem)] px-4 py-2 text-sm font-medium transition-colors duration-300 ease-in-out " + BUTTON_TEXT}
            style={{ color: !isYearly ? "var(--template-accent-text, white)" : "var(--template-muted)" }}
          >
            Monthly
          </button>
          <button
            onClick={() => setIsYearly(true)}
            aria-pressed={isYearly}
            className={"relative z-10 rounded-[var(--template-button-radius,0.375rem)] px-4 py-2 text-sm font-medium transition-colors duration-300 ease-in-out " + BUTTON_TEXT}
            style={{ color: isYearly ? "var(--template-accent-text, white)" : "var(--template-muted)" }}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* SLIDER SECTION */}
      <div className="mx-auto mb-12 max-w-2xl">
        <div className="mb-4 flex items-center justify-between">
          {/* LABEL - Size: sm, Weight: 500 */}
          <p className="text-sm font-medium text-[color:var(--template-ink)]">Monthly visitors</p>
          {/* VALUE - Size: xl, Weight: 600 */}
          <p className="text-xl font-semibold text-[color:var(--template-ink)] tabular-nums">
            {TRAFFIC_STEPS[stepIndex].label}
          </p>
        </div>

        {/* 🎨 BRAND COLOR: Slider fill and thumb */}
        <div
          onClick={handleSliderClick}
          className="relative mb-3 h-1.5 cursor-pointer rounded-full bg-[var(--template-border)]"
        >
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all duration-150"
            style={{ width: \`\${percentage}%\`, backgroundColor: primaryColor }}
          />
          <div
            className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-150"
            style={{
              left: \`\${percentage}%\`,
              backgroundColor: primaryColor,
              boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
            }}
          />
        </div>

        {/* LABELS - Size: xs, Weight: 400 */}
        <div className="flex justify-between">
          {TRAFFIC_STEPS.map((step, i) => (
            <button
              key={step.label}
              onClick={() => setStepIndex(i)}
              aria-pressed={i === stepIndex}
              className="text-xs transition"
              style={{
                fontWeight: i === stepIndex ? 500 : 400,
                color: i === stepIndex ? primaryColor : "var(--template-muted)",
              }}
            >
              {step.label}
            </button>
          ))}
        </div>
      </div>

      {/* PLAN CARDS */}
      <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-3">
        {PLANS.map((plan) => {
          const price = getPrice(plan, isYearly);
          return (
            <div
              key={plan.key}
              className="relative flex flex-col rounded-xl border border-solid p-6"
              style={{
                backgroundColor: plan.highlighted ? "var(--template-secondary)" : "var(--template-surface)",
                borderColor: plan.highlighted ? primaryColor : "var(--template-border)",
              }}
            >
              {/* 🎨 BRAND COLOR: Popular badge */}
              {plan.highlighted && (
                <div
                  className="absolute inset-x-0 -top-3 mx-auto w-fit rounded-full bg-[var(--template-accent-secondary)] px-3 py-1 text-sm font-medium text-[color:var(--template-accent-secondary-text)]"
                >
                  Popular
                </div>
              )}

              {/* PLAN NAME - Size: sm, Weight: 500 */}
              <p className="mb-1 text-sm font-medium text-[color:var(--template-muted)]">
                {plan.name}
              </p>

              {/* PRICE - Size: 3xl, Weight: 600 */}
              <div className="mb-1 flex items-baseline gap-1">
                <span className="text-3xl font-semibold text-[color:var(--template-ink)] tabular-nums">
                  {formatUSD(price)}
                </span>
                <span className={TYPE.small}>/mo</span>
              </div>

              {/* DESCRIPTION - Size: sm, Weight: 400 */}
              <p className={"mb-6 " + TYPE.small}>
                {plan.description}
              </p>

              {/* FEATURES - Size: sm, Weight: 400 */}
              <div className="mb-6 flex flex-1 flex-col gap-3">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2 text-sm text-[color:var(--template-ink)]">
                    {/* 🎨 BRAND COLOR: Checkmark */}
                    <span style={{ color: primaryColor }}>
                      <Check />
                    </span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* 🎨 BRAND COLOR: CTA button */}
              <button
                className={"rounded-[var(--template-button-radius,0.5rem)] px-4 py-2.5 text-sm font-medium text-[color:var(--template-accent-text,white)] transition hover:opacity-90 " + BUTTON_TEXT}
                style={{ backgroundColor: primaryColor }}
              >
                {plan.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* NOTE - Size: sm, Weight: 400 */}
      <p className={"mt-8 text-center " + TYPE.small}>{billingNote}</p>
    </div>
  );
}

export default declareComponent(PricingSection, {
  name: "Pricing Section",
  description: "Three-tiered pricing with a web traffic slider that adjusts prices dynamically.",
  group: "Sections",
  props: {
    heading: props.Text({
      name: "Heading",
      defaultValue: "Plans that scale with you",
    }),
    subheading: props.Text({
      name: "Subheading",
      defaultValue:
        "Transparent pricing that grows alongside your traffic. No surprises, cancel anytime.",
    }),
    billingNote: props.Text({
      name: "Billing Note",
      defaultValue: "All plans billed monthly. Annual billing saves 20%.",
    }),
    primaryColor: props.Text({
      name: "Primary Brand Color",
      defaultValue: ${primaryColor},
    }),
  },
});
`;
}

export const PRICING_CALCULATOR_SOURCE = createPricingCalculatorSource();
